import { useMemo } from 'react';
import { ErrorLike, WatchQueryFetchPolicy } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useTenantFeatureGates } from './useTenantFeatureGates';
import { TenantFeatureGatesEnum } from '@constants/tenantFeatureGates';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { virtualAgentFiltersQuery } from '@pages/monitoring/ConversationsPage/data/virtual-agents';

export interface UseVirtualAgentFeatureGateProps {
  skip?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export interface VirtualAgentFeatureGate {
  configuration: boolean;
  reporting: boolean;
  dashboardMfe: boolean;
  jobBooking: boolean;
  followUps: boolean;
  loading: boolean;
  /**
   * Set when the tenant feature gate query failed. Callers must not read a
   * `false` feature as "disabled" while this is set — the answer is unknown.
   */
  error?: ErrorLike;
}

/**
 * Hook to check if Virtual Agent features are enabled for the organization.
 *
 * Features are enabled if ANY of the following conditions are met:
 * 1. The tenant feature gate `EnableVoiceAgentForContactCenterPro` is enabled for any tenant in the org
 * 2. The corresponding LaunchDarkly feature flag is enabled
 * 3. VA defaults are configured in the VA config service (via virtualAgentFilters query)
 *
 * The VA config service check uses cache-first policy and treats errors as false,
 * ensuring it never blocks or breaks the hook if the service is unavailable.
 *
 * `loading` applies to `configuration`/`reporting`: it stays true until the
 * tenant gate answers, unless a condition that enables them is already known
 * to be true (VAH-1243).
 *
 * @param skip - Skip the feature gate query (default: false)
 * @param fetchPolicy - Apollo fetch policy for the tenant gate query (default: 'network-only')
 * @returns Object with configuration, jobBooking, followUps booleans, loading state and gate error
 */
export const useVirtualAgentFeatureGate = ({
  skip = false,
  fetchPolicy = 'network-only',
}: UseVirtualAgentFeatureGateProps = {}): VirtualAgentFeatureGate => {
  const { user } = useAuth();
  const tenantIds = useMemo(
    () => user?.org?.tenants?.map((t) => t.id) ?? [],
    [user],
  );

  const {
    [FeatureFlagKeys.featureVirtualAgentConfiguration]:
      featureVirtualAgentConfiguration,
    [FeatureFlagKeys.featureVirtualAssistantJobBooking]:
      featureVirtualAssistantJobBooking,
    [FeatureFlagKeys.featureVirtualAssistantFollowUps]:
      featureVirtualAssistantFollowUps,
    [FeatureFlagKeys.featureVaDashboardMfe]: featureVaDashboardMfe,
  } = useFeatureFlags();

  const {
    data: tenantGateData,
    loading: tenantGateLoading,
    error: tenantGateError,
  } = useTenantFeatureGates({
    tenantIds,
    featureGateKeys: [
      TenantFeatureGatesEnum.EnableVoiceAgentForContactCenterPro,
    ],
    skip,
    fetchPolicy,
  });

  // Only query VA config when no tenants are in the user context (pre-go-live).
  // Once tenants are populated, the tenant feature gate handles enablement.
  const hasNoTenants = tenantIds.length === 0;

  const {
    data: vaConfigData,
    loading: vaConfigLoading,
    error: vaConfigError,
  } = useQuery(virtualAgentFiltersQuery, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    skip: skip || !hasNoTenants,
  });

  const isFeatureGateEnabled = useMemo(() => {
    const featureGate = tenantGateData?.tenantFeatureGates.find(
      (gate) =>
        gate.key === TenantFeatureGatesEnum.EnableVoiceAgentForContactCenterPro,
    );
    const value = featureGate?.value;
    return value && 'boolValue' in value ? value.boolValue : false;
  }, [tenantGateData]);

  // VA defaults are considered configured if there are no tenants (pre-go-live),
  // the query succeeded without error, and returned a valid defaultId (non-zero).
  // The query returns null when no VA config exists for the contact center, so
  // we check both that virtualAgentFilters is present and that defaultId is
  // truthy to distinguish "actually configured" from "not configured".
  const vaDefaultsConfigured =
    hasNoTenants &&
    !vaConfigError &&
    !!vaConfigData?.virtualAgentFilters?.defaultSettings?.defaultId;

  const configuration =
    isFeatureGateEnabled ||
    Boolean(featureVirtualAgentConfiguration ?? false) ||
    vaDefaultsConfigured;

  // Keep loading until `configuration` is known. It is decided only by the
  // tenant gate, the configuration flag, or VA defaults — the job-booking and
  // follow-ups flags decide other features, so they must not end the wait.
  const loading = !configuration && (tenantGateLoading || vaConfigLoading);

  return {
    configuration,
    reporting: configuration,
    dashboardMfe: configuration && Boolean(featureVaDashboardMfe ?? false),
    jobBooking:
      isFeatureGateEnabled ||
      Boolean(featureVirtualAssistantJobBooking ?? false) ||
      vaDefaultsConfigured,
    followUps:
      isFeatureGateEnabled ||
      Boolean(featureVirtualAssistantFollowUps ?? false) ||
      vaDefaultsConfigured,
    loading,
    error: tenantGateError,
  };
};
